import {
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { Link, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { DarkOrdersCard } from "../components/DarkOrdersCard";
import { DeliveryTrendsCard } from "../components/DeliveryTrendsCard";
import { RecentOrdersCard } from "../components/RecentOrdersCard";
import { TransporterActivityPanel } from "../components/TransporterActivityPanel";
import { useDispensaryDashboard } from "../hooks/useDispensaryDashboard";
import { useDispensaryDashboardLive } from "../hooks/useDispensaryDashboardLive";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function WelcomeHeader() {
  const { dispensaryName, activeTransporterCount, isLoading } = useDispensaryDashboard();

  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-2xl font-bold text-[#111111]">
          {isLoading ? <span className="inline-block h-8 w-48 animate-pulse rounded bg-[#E8E8E8]" /> : `Welcome back, ${dispensaryName}`}
        </h2>
        <div className="mt-1.5 flex items-center gap-1.5">
          <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green" />
          <span className="text-sm text-[#555555]">
            {activeTransporterCount} licensed transporters en route
          </span>
        </div>
      </div>
      <Link
        to="/workspace/orders"
        className="flex items-center gap-2 rounded-xl bg-[#111111] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#333333]"
      >
        <PlusIcon className="h-4 w-4" />
        Create Order
      </Link>
    </div>
  );
}

function MetricCard({
  label,
  value,
  delta,
  trend,
  illustration,
  isLoading,
}: {
  label: string;
  value: string;
  delta?: string;
  trend?: "up" | "down";
  illustration: ReactNode;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="h-[120px] animate-pulse rounded-2xl border border-[#EBEBEB] bg-white p-5">
        <div className="mb-3 h-3 w-24 rounded bg-[#F0F0F0]" />
        <div className="h-10 w-16 rounded bg-[#F0F0F0]" />
      </div>
    );
  }
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white p-5 shadow-[0_1px_4px_rgba(0,0,0,.06)]">
      <p className="mb-2 text-sm font-medium text-[#777777]">{label}</p>
      <p className="text-4xl font-bold text-[#111111]">{value}</p>
      {delta ? (
        <p className={`mt-1 text-xs font-medium ${trend === "up" ? "text-[#2D6B2D]" : "text-[#555555]"}`}>
          {delta}
        </p>
      ) : null}
      <div className="pointer-events-none absolute bottom-4 right-4 opacity-90">{illustration}</div>
    </div>
  );
}

const CalendarIllustration = () => <CalendarDaysIcon className="h-14 w-14 text-[#CCCCCC]" />;
const TruckIllustration = () => <TruckIcon className="h-14 w-10 text-[#CCCCCC]" />;
const ClockIllustration = () => <ClockIcon className="h-12 w-12 text-[#CCCCCC]" />;
const DocumentsIllustration = () => <ClipboardDocumentListIcon className="h-12 w-14 text-[#CCCCCC]" />;

export default function Dashboard() {
  const headerSearch = useRouterState({
    select: (s) =>
      s.location.pathname.startsWith("/workspace/dashboard")
        ? new URLSearchParams(s.location.search).get("q")?.trim() ?? ""
        : "",
  });
  const { dispensaryId, orgId, allLocations } = useDispensaryWorkspace();
  const aggregate = allLocations && orgId != null;
  const {
    ordersToday,
    ordersOnTheWay,
    avgDeliveryMinutes,
    pendingOrders,
    ordersTodayDelta,
    onTheWayDelta,
    pendingDelta,
    isLoading,
  } = useDispensaryDashboard();

  // Live socket is per-store; in "All locations" mode it no-ops and the
  // dashboard refreshes via react-query intervals instead.
  useDispensaryDashboardLive(dispensaryId);

  if (!dispensaryId && !aggregate) {
    return (
      <div className="min-h-[40vh] rounded-2xl border border-[#EBEBEB] bg-white p-8 text-center text-sm text-[#555555] shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <p className="font-medium text-[#111111]">No dispensary scope</p>
        <p className="mt-2">Pick a location in the switcher above, or ask an admin to link your account.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F3] p-7">
      <WelcomeHeader />

      {isLoading ? (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[120px] animate-pulse rounded-2xl border border-[#EBEBEB] bg-white p-5">
              <div className="mb-3 h-3 w-24 rounded bg-[#F0F0F0]" />
              <div className="h-10 w-16 rounded bg-[#F0F0F0]" />
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Orders Today"
            value={String(ordersToday)}
            delta={ordersTodayDelta !== 0 ? `+${ordersTodayDelta}% vs last week` : undefined}
            trend="up"
            illustration={<CalendarIllustration />}
          />
          <MetricCard
            label="On The Way"
            value={String(ordersOnTheWay)}
            delta={onTheWayDelta !== 0 ? `+${onTheWayDelta}% vs last week` : undefined}
            trend="up"
            illustration={<TruckIllustration />}
          />
          <MetricCard
            label="Avg. Delivery Time"
            value={`${avgDeliveryMinutes || "—"} min`}
            illustration={<ClockIllustration />}
          />
          <MetricCard
            label="Pending Orders"
            value={String(pendingOrders)}
            delta={pendingDelta !== 0 ? `+${pendingDelta}` : undefined}
            trend="up"
            illustration={<DocumentsIllustration />}
          />
        </div>
      )}

      <div className="flex flex-col gap-5 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <DarkOrdersCard headerSearch={headerSearch} />
          <DeliveryTrendsCard />
          <RecentOrdersCard headerSearch={headerSearch} />
        </div>
        <TransporterActivityPanel headerSearch={headerSearch} />
      </div>
    </div>
  );
}
